<div class="bg-base-100 border border-base-300 rounded-lg overflow-hidden h-full flex flex-col">
  <ng-container *ngIf="selectedTemplateId() !== null; else emptyState">
    <!-- Field rules -->
    <section class="border-b border-base-300">
      <div class="flex items-center justify-between px-4 py-3 bg-base-200">
        <h3 class="text-sm font-bold text-primary uppercase tracking-wide">
          Field Rules ({{ sortedFieldRules().length }})
        </h3>
        <button
          type="button"
          (click)="openCreate('field')"
          class="h-8 px-4 border border-base-300 rounded text-sm text-dark font-medium hover:bg-info hover:text-white transition-colors"
        >
          + Add Field Rule
        </button>
      </div>
      <div class="overflow-x-auto">
        <table class="table w-full text-sm">
          <thead>
            <tr class="h-[44px] bg-base-200">
              <th class="py-2 px-4 text-left font-bold text-primary">Rule Type</th>
              <th class="py-2 px-4 text-left font-bold text-primary">Params</th>
              <th class="py-2 px-4 text-left font-bold text-primary">Severity</th>
              <th class="py-2 px-4 text-left font-bold text-primary">Group</th>
              <th class="py-2 px-4 text-left font-bold text-primary">Active</th>
              <th class="py-2 px-4 font-bold text-primary">Actions</th>
            </tr>
          </thead>
          <tbody *ngIf="sortedFieldRules().length > 0; else noField">
            <tr
              *ngFor="let rule of sortedFieldRules(); trackBy: trackRuleId"
              class="h-[44px] even:bg-base-200"
            >
              <td class="py-2 px-4 text-primary font-medium">{{ rule.rule_type }}</td>
              <td class="py-2 px-4 text-primary text-xs font-mono">{{ paramsSummary(rule.params) }}</td>
              <td class="py-2 px-4">
                <span
                  class="badge badge-sm"
                  [class.badge-error]="rule.severity === 'ERROR'"
                  [class.badge-warning]="rule.severity === 'WARNING'"
                >
                  {{ rule.severity }}
                </span>
              </td>
              <td class="py-2 px-4 text-primary text-xs">{{ rule.rule_group || '—' }}</td>
              <td class="py-2 px-4">
                <input
                  type="checkbox"
                  class="toggle toggle-sm"
                  [checked]="rule.is_active"
                  (change)="toggleFieldActive(rule)"
                  [attr.aria-label]="'Toggle active for rule ' + rule.id"
                />
              </td>
              <td class="py-2 px-4">
                <div class="flex items-center gap-2">
                  <button
                    class="cursor-pointer text-primary-focus hover:text-primary transition-colors"
                    (click)="openEdit(rule, 'field')"
                    aria-label="Edit rule"
                  >
                    <fa-icon [icon]="'edit'" class="h-4 w-4"></fa-icon>
                  </button>
                  <button
                    class="cursor-pointer text-primary-focus hover:text-error transition-colors"
                    (click)="beginDelete(rule, 'field')"
                    aria-label="Delete rule"
                  >
                    <fa-icon [icon]="'trash'" class="h-4 w-4"></fa-icon>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
          <ng-template #noField>
            <tr>
              <td colspan="6" class="py-6 text-center text-sm text-base-content/70">
                No field rules attached to this template.
              </td>
            </tr>
          </ng-template>
        </table>
      </div>
    </section>

    <!-- Cross-field rules -->
    <section>
      <div class="flex items-center justify-between px-4 py-3 bg-base-200">
        <h3 class="text-sm font-bold text-primary uppercase tracking-wide">
          Cross-Field Rules ({{ sortedCrossFieldRules().length }})
        </h3>
        <button
          type="button"
          (click)="openCreate('cross')"
          class="h-8 px-4 border border-base-300 rounded text-sm text-dark font-medium hover:bg-info hover:text-white transition-colors"
        >
          + Add Cross-Field Rule
        </button>
      </div>
      <div class="overflow-x-auto">
        <table class="table w-full text-sm">
          <thead>
            <tr class="h-[44px] bg-base-200">
              <th class="py-2 px-4 text-left font-bold text-primary">Rule Type</th>
              <th class="py-2 px-4 text-left font-bold text-primary">Templates</th>
              <th class="py-2 px-4 text-left font-bold text-primary">Params</th>
              <th class="py-2 px-4 text-left font-bold text-primary">Severity</th>
              <th class="py-2 px-4 text-left font-bold text-primary">Active</th>
              <th class="py-2 px-4 font-bold text-primary">Actions</th>
            </tr>
          </thead>
          <tbody *ngIf="sortedCrossFieldRules().length > 0; else noCross">
            <tr
              *ngFor="let rule of sortedCrossFieldRules(); trackBy: trackRuleId"
              class="h-[44px] even:bg-base-200"
            >
              <td class="py-2 px-4 text-primary font-medium">{{ rule.rule_type }}</td>
              <td class="py-2 px-4 text-primary text-xs">
                {{ rule.left_template }} &larr; {{ rule.right_template }}
              </td>
              <td class="py-2 px-4 text-primary text-xs font-mono">{{ paramsSummary(rule.params) }}</td>
              <td class="py-2 px-4">
                <span
                  class="badge badge-sm"
                  [class.badge-error]="rule.severity === 'ERROR'"
                  [class.badge-warning]="rule.severity === 'WARNING'"
                >
                  {{ rule.severity }}
                </span>
              </td>
              <td class="py-2 px-4">
                <input
                  type="checkbox"
                  class="toggle toggle-sm"
                  [checked]="rule.is_active"
                  (change)="toggleCrossActive(rule)"
                  [attr.aria-label]="'Toggle active for rule ' + rule.id"
                />
              </td>
              <td class="py-2 px-4">
                <div class="flex items-center gap-2">
                  <button
                    class="cursor-pointer text-primary-focus hover:text-primary transition-colors"
                    (click)="openEdit(rule, 'cross')"
                    aria-label="Edit cross-field rule"
                  >
                    <fa-icon [icon]="'edit'" class="h-4 w-4"></fa-icon>
                  </button>
                  <button
                    class="cursor-pointer text-primary-focus hover:text-error transition-colors"
                    (click)="beginDelete(rule, 'cross')"
                    aria-label="Delete cross-field rule"
                  >
                    <fa-icon [icon]="'trash'" class="h-4 w-4"></fa-icon>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
          <ng-template #noCross>
            <tr>
              <td colspan="6" class="py-6 text-center text-sm text-base-content/70">
                No cross-field rules involve this template.
              </td>
            </tr>
          </ng-template>
        </table>
      </div>
    </section>
  </ng-container>

  <ng-template #emptyState>
    <div class="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center text-base-content/70">
      <fa-icon [icon]="'hand-pointer'" class="h-8 w-8 mb-4 text-base-content/60"></fa-icon>
      <p class="text-base">Pick a template from the list to view and edit its rules.</p>
    </div>
  </ng-template>
</div>

<!-- Editor modal (lives at the panel level so it can be reused for field + cross) -->
<app-rule-editor-modal
  [modalId]="editorModalId"
  [mode]="editorMode"
  (closed)="onEditorClosed()"
></app-rule-editor-modal>

<!-- Delete confirmation -->
<app-delete-rule-modal
  [modalId]="deleteModalId"
  [pending]="pendingDelete"
  (confirm)="confirmDelete()"
  (cancel)="cancelDelete()"
></app-delete-rule-modal>
